import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Chip,
  LinearProgress,
  Fade,
  Slide,
  Zoom,
  Stack,
  Avatar,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  Tab,
  Tabs,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ComposedChart,
  Scatter,
  ScatterChart,
  ZAxis,
  Treemap,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectProperties,
  selectGlobalProperty,
  selectLocalProperty,
  setLocalProperty,
} from '../../redux/propertySlice';
import { ToastContainer } from "react-toastify";
import { GetPropWiseBedCalendarMonth } from '../../Api/PropertyMst/Api';
import { toast } from 'react-toastify';
import {
  TrendingUp,
  TrendingDown,
  Hotel,
  Bed,
  People,
  CalendarToday,
  Assessment,
  Download,
  Refresh,
  ExpandMore,
  ChevronRight,
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Room as RoomIcon,
  Apartment as ApartmentIcon,
  Visibility as ViewIcon,
  Layers as LayersIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterIcon,
  Weekend,
  KingBed,
  SingleBed,
  Bathtub,
  Wifi,
  Coffee,
  Tv,
  AcUnit,
  Pool,
  FitnessCenter,
  Restaurant,
  LocalParking,
  Elevator,
  Security,
  Balcony,
  SmokeFree,
  Pets,
  TableChart as TableChartIcon,
  CheckCircle,
  Cancel,
  Pending,
  Build,
  PersonAdd,
  TransferWithinAStation,
  Notifications,
  AccessTime,
  ArrowUpward,
  ArrowDownward,
  HourglassEmpty,
  Info as InfoIcon,
  CurrencyRupee,
  GridView,
  ViewList,
  ViewModule,
  DonutLarge,
  ShowChart,
  BubbleChart,
  AccountCircle,
  AttachMoney,
  EventNote,
  LocationCity,
  Analytics,
  Speed,
} from '@mui/icons-material';

const STATUS_COLORS = {
  O: '#f05c50',
  A: '#046A38',
  P: '#FF671F',
  L: '#8B5CF6',
  B: '#635bff',
  C: '#c971bb',
  M: '#800000',
  R: '#47a1ba',
};

const STATUS_LABELS = {
  O: 'Occupied',
  A: 'Available',
  P: 'Partially Booked',
  L: 'Left',
  B: 'Booked',
  C: 'Checked Out',
  M: 'Maintenance',
  R: 'Reserved',
};

const STATUS_ICONS = {
  O: <People sx={{ fontSize: 18 }} />,
  A: <Bed sx={{ fontSize: 18 }} />,
  P: <HourglassEmpty sx={{ fontSize: 18 }} />,
  L: <Cancel sx={{ fontSize: 18 }} />,
  B: <CalendarToday sx={{ fontSize: 18 }} />,
  C: <TrendingDown sx={{ fontSize: 18 }} />,
  M: <Build sx={{ fontSize: 18 }} />,
  R: <KingBed sx={{ fontSize: 18 }} />,
};

const ALL_STATUS_KEYS = ['O', 'A', 'P', 'L', 'B', 'C', 'M', 'R'];


const CHART_COLORS = ['#667eea', '#764ba2', '#f05c50', '#046A38', '#FF671F', '#8B5CF6', '#635bff', '#c971bb', '#47a1ba', '#800000'];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

export default function BedManagementDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const properties = useSelector(selectProperties);
  const globalProperty = useSelector(selectGlobalProperty);
  const localProperty = useSelector(state => selectLocalProperty(state, 'bedManagement'));
  const selectedProperty = localProperty || globalProperty;

  const [dateFrom, setDateFrom] = useState(dayjs());
  const [dateTo, setDateTo] = useState(dayjs().add(12, 'months'));
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [viewMode, setViewMode] = useState('overview');
  const [selectedBuilding, setSelectedBuilding] = useState('all');
  const [selectedFloor, setSelectedFloor] = useState('all');
  const [expandedFloors, setExpandedFloors] = useState({});
  const [expandedRooms, setExpandedRooms] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [allFetchedData, setAllFetchedData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [chartType, setChartType] = useState('bar');
  const [sortField, setSortField] = useState('rent');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedResident, setSelectedResident] = useState(null);

  const fetchAllDashboardData = useCallback(async () => {
    if (!selectedProperty) {
      toast.error('Please select a property');
      return;
    }

    try {
      setIsLoading(true);
      let allData = [];
      let currentPage = 1;
      const pageSize = 10000;
      let totalCount = 0;

      const initialPayload = {
        PropId: selectedProperty,
        BookDtFrom: dateFrom.format('YYYY-MM-DD'),
        CheckoutDt: dateTo.format('YYYY-MM-DD'),
        Flag: '',
        currentPage: 1,
        PageSize: pageSize,
        SearchText: '',
      };

      const initialResponse = await GetPropWiseBedCalendarMonth(initialPayload);

      if (initialResponse?.data?.Status === 0) {
        totalCount = initialResponse.data.TotalCount || 0;
        const firstPageData = initialResponse.data.Data || [];
        allData = [...firstPageData];

        const totalPages = Math.ceil(totalCount / pageSize);

        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            const payload = {
              PropId: selectedProperty,
              BookDtFrom: dateFrom.format('YYYY-MM-DD'),
              CheckoutDt: dateTo.format('YYYY-MM-DD'),
              Flag: '',
              currentPage: page,
              PageSize: pageSize,
              SearchText: '',
            };
            promises.push(GetPropWiseBedCalendarMonth(payload));
          }

          const remainingResponses = await Promise.all(promises);
          remainingResponses.forEach(response => {
            if (response?.data?.Status === 0) {
              const data = response.data.Data || [];
              allData = [...allData, ...data];
            }
          });
        }

        setAllFetchedData(allData);
        setTotalRecords(totalCount);

        const processedData = processDashboardData(allData, totalCount);
        setDashboardData(processedData);
        toast.success(`Loaded ${processedData.totalBeds} beds`);
      } else {
        toast.error(initialResponse?.data?.Message || 'Error fetching data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProperty, dateFrom, dateTo]);

  const processDashboardData = (apiData, totalCount) => {
    if (!apiData || apiData.length === 0) {
      return {
        totalBeds: totalCount || 0,
        totalBuildings: 0,
        totalRooms: 0,
        occupiedBeds: 0,
        availableBeds: 0,
        partiallyBookedBeds: 0,
        leftBeds: 0,
        bookedBeds: 0,
        checkedOutBeds: 0,
        maintenanceBeds: 0,
        reservedBeds: 0,
        todayCheckins: 0,
        todayCheckouts: 0,
        statusSummary: {},
        buildingData: {},
        recentActivities: [],
        floorData: {},
        roomData: {},
        bedData: {},
        occupancyByBuilding: [],
        bedStatusData: [],
        monthlyChartData: [],
        performanceMetrics: {
          occupancyRate: 0,
          availableRate: 0,
          partiallyBookedRate: 0,
          leftRate: 0,
          bookedRate: 0,
          checkedOutRate: 0,
          maintenanceRate: 0,
          reservedRate: 0,
        },
        residentData: [],
        rentDistribution: [],
        buildingRevenue: [],
        statusTrend: [],
      };
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = dayjs().format('YYYY-MM-DD');
    const currentMonthId = dayjs().month() + 1;
    const currentYear = dayjs().year();

    const bedData = {};
    const residentMap = {};
    const rentData = [];

    apiData.forEach(item => {
      const bedId = item.RoomBedId;
      if (!bedId) return;

      const status = item.BedStatus || '';
      const building = item.PropertyName || item.BuildingName || 'Unnamed Building';
      const floor = item.FloorName || 'Unnamed Floor';
      const roomNo = item.RoomNo || 'Unnamed Room';
      const bedName = item.RoombedName || 'Unnamed Bed';
      const residentName = item.ResidentName || 'Vacant';
      const checkinDate = item.CheckinDate || '';
      const checkoutDate = item.CheckoutDate || '';
      const bedRent = item.BedRent || 0;
      const isCurrentMonthRecord = item.MonthId === currentMonthId && item.YearNm === currentYear;

      if (!bedData[bedId]) {
        bedData[bedId] = {
          id: bedId,
          name: bedName,
          roomNo,
          floor,
          building,
          status,
          resident: residentName,
          rent: bedRent,
          checkinDate,
          checkoutDate,
          monthId: item.MonthId,
          year: item.YearNm,
        };
      } else if (isCurrentMonthRecord) {
        bedData[bedId].status = status;
        bedData[bedId].resident = residentName;
        bedData[bedId].checkinDate = checkinDate;
        bedData[bedId].checkoutDate = checkoutDate;
        bedData[bedId].rent = bedRent;
      }

      if (residentName && residentName !== 'Vacant') {
        if (!residentMap[residentName]) {
          residentMap[residentName] = {
            name: residentName,
            bed: bedName,
            room: roomNo,
            floor,
            building,
            rent: bedRent,
            status,
            checkin: checkinDate,
            checkout: checkoutDate,
          };
        }
        rentData.push({
          resident: residentName,
          rent: bedRent,
          bed: bedName,
          room: roomNo,
          building,
          floor,
        });
      }
    });

    const buildingData = {};
    const floorData = {};
    const roomData = {};
    const statusCount = {};
    const activities = [];

    ALL_STATUS_KEYS.forEach(key => { statusCount[key] = 0; });

    let occupiedBeds = 0;
    let availableBeds = 0;
    let partiallyBookedBeds = 0;
    let leftBeds = 0;
    let bookedBeds = 0;
    let checkedOutBeds = 0;
    let maintenanceBeds = 0;
    let reservedBeds = 0;
    let todayCheckins = 0;
    let todayCheckouts = 0;

    Object.values(bedData).forEach(bed => {
      const { building, floor, roomNo, name: bedName, status, resident: residentName, checkinDate, checkoutDate, rent } = bed;

      if (!buildingData[building]) {
        buildingData[building] = {
          name: building,
          totalBeds: 0,
          occupiedBeds: 0,
          availableBeds: 0,
          partiallyBookedBeds: 0,
          leftBeds: 0,
          bookedBeds: 0,
          checkedOutBeds: 0,
          maintenanceBeds: 0,
          reservedBeds: 0,
          totalRent: 0,
          floors: {},
          statusCounts: {},
          residents: [],
        };
        ALL_STATUS_KEYS.forEach(key => { buildingData[building].statusCounts[key] = 0; });
      }

      if (!floorData[floor]) {
        floorData[floor] = {
          name: floor,
          building,
          totalBeds: 0,
          occupiedBeds: 0,
          availableBeds: 0,
          partiallyBookedBeds: 0,
          leftBeds: 0,
          bookedBeds: 0,
          checkedOutBeds: 0,
          maintenanceBeds: 0,
          reservedBeds: 0,
          totalRent: 0,
          rooms: {},
        };
      }

      const roomKey = `${building}-${floor}-${roomNo}`;
      if (!roomData[roomKey]) {
        roomData[roomKey] = { roomNo, floor, building, beds: [], bedStatuses: [], bedRents: [], residents: [] };
      }
      if (!roomData[roomKey].beds.includes(bedName)) {
        roomData[roomKey].beds.push(bedName);
        roomData[roomKey].bedStatuses.push(status);
        roomData[roomKey].bedRents.push(rent);
        roomData[roomKey].residents.push(residentName);
      }

      if (status && statusCount.hasOwnProperty(status)) {
        statusCount[status]++;
      }

      buildingData[building].totalBeds++;
      buildingData[building].totalRent += rent;
      if (residentName && residentName !== 'Vacant') {
        if (!buildingData[building].residents.includes(residentName)) {
          buildingData[building].residents.push(residentName);
        }
      }
      
      floorData[floor].totalBeds++;
      floorData[floor].totalRent += rent;
      
      if (status && buildingData[building].statusCounts.hasOwnProperty(status)) {
        buildingData[building].statusCounts[status]++;
      }

      switch (status) {
        case 'O':
          buildingData[building].occupiedBeds++;
          floorData[floor].occupiedBeds++;
          occupiedBeds++;
          break;
        case 'A':
          buildingData[building].availableBeds++;
          floorData[floor].availableBeds++;
          availableBeds++;
          break;
        case 'P':
          buildingData[building].partiallyBookedBeds++;
          floorData[floor].partiallyBookedBeds++;
          partiallyBookedBeds++;
          break;
        case 'L':
          buildingData[building].leftBeds++;
          floorData[floor].leftBeds++;
          leftBeds++;
          break;
        case 'B':
          buildingData[building].bookedBeds++;
          floorData[floor].bookedBeds++;
          bookedBeds++;
          break;
        case 'C':
          buildingData[building].checkedOutBeds++;
          floorData[floor].checkedOutBeds++;
          checkedOutBeds++;
          break;
        case 'M':
          buildingData[building].maintenanceBeds++;
          floorData[floor].maintenanceBeds++;
          maintenanceBeds++;
          break;
        case 'R':
          buildingData[building].reservedBeds++;
          floorData[floor].reservedBeds++;
          reservedBeds++;
          break;
        default:
          break;
      }

      if (checkinDate === today && status === 'O') {
        todayCheckins++;
        activities.push({
          id: `checkin-${bed.id}`,
          title: `${residentName} checked in to ${bedName}, ${roomNo}, ${floor}`,
          time: 'Today',
          type: 'checkin',
          icon: <PersonAdd sx={{ fontSize: 16 }} />,
        });
      }
      if (checkoutDate === today && (status === 'C' || status === 'L')) {
        todayCheckouts++;
        activities.push({
          id: `checkout-${bed.id}`,
          title: `${residentName} checked out from ${bedName}, ${roomNo}, ${floor}`,
          time: 'Today',
          type: 'checkout',
          icon: <Cancel sx={{ fontSize: 16 }} />,
        });
      }
    });

    const totalBeds = Object.keys(bedData).length || totalCount || 0;

    const monthlyData = {};
    apiData.forEach(item => {
      const monthId = item.MonthId;
      const year = item.YearNm;
      const status = item.BedStatus || '';
      const monthKey = `${year}-${monthId}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthNames[monthId - 1] || monthId,
          year,
          monthId,
          Occupied: 0,
          Available: 0,
          Booked: 0,
          'Partially Booked': 0,
          Left: 0,
          'Checked Out': 0,
          Maintenance: 0,
          Reserved: 0,
          total: 0,
          totalRent: 0,
        };
      }

      if (status === 'O') monthlyData[monthKey].Occupied++;
      else if (status === 'A') monthlyData[monthKey].Available++;
      else if (status === 'B') monthlyData[monthKey].Booked++;
      else if (status === 'P') monthlyData[monthKey]['Partially Booked']++;
      else if (status === 'L') monthlyData[monthKey].Left++;
      else if (status === 'C') monthlyData[monthKey]['Checked Out']++;
      else if (status === 'M') monthlyData[monthKey].Maintenance++;
      else if (status === 'R') monthlyData[monthKey].Reserved++;

      monthlyData[monthKey].total++;
      monthlyData[monthKey].totalRent += (item.BedRent || 0);
    });

    const monthlyChartData = Object.values(monthlyData).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.monthId - b.monthId;
    });

    const occupancyByBuilding = Object.keys(buildingData).map(key => ({
      name: key,
      occupancy: buildingData[key].totalBeds > 0
        ? Math.round((buildingData[key].occupiedBeds / buildingData[key].totalBeds) * 100)
        : 0,
      total: buildingData[key].totalBeds,
      occupied: buildingData[key].occupiedBeds,
      available: buildingData[key].availableBeds,
      partiallyBooked: buildingData[key].partiallyBookedBeds || 0,
      left: buildingData[key].leftBeds || 0,
      booked: buildingData[key].bookedBeds || 0,
      checkedOut: buildingData[key].checkedOutBeds || 0,
      maintenance: buildingData[key].maintenanceBeds || 0,
      reserved: buildingData[key].reservedBeds || 0,
      totalRent: buildingData[key].totalRent || 0,
      residentCount: buildingData[key].residents ? buildingData[key].residents.length : 0,
    }));

    const bedStatusData = [];
    const statusOrder = ['O', 'A', 'P', 'L', 'B', 'C', 'M', 'R'];
    statusOrder.forEach(key => {
      const value = statusCount[key] || 0;
      if (value > 0) {
        bedStatusData.push({ name: STATUS_LABELS[key] || key, value, key });
      }
    });

    
    const rentDistribution = [];
    const rentBuckets = {
      '0-5000': 0,
      '5001-10000': 0,
      '10001-15000': 0,
      '15001-20000': 0,
      '20001-30000': 0,
      '30000+': 0,
    };
    
    Object.values(bedData).forEach(bed => {
      const rent = bed.rent || 0;
      if (rent <= 5000) rentBuckets['0-5000']++;
      else if (rent <= 10000) rentBuckets['5001-10000']++;
      else if (rent <= 15000) rentBuckets['10001-15000']++;
      else if (rent <= 20000) rentBuckets['15001-20000']++;
      else if (rent <= 30000) rentBuckets['20001-30000']++;
      else rentBuckets['30000+']++;
    });
    
    Object.entries(rentBuckets).forEach(([range, count]) => {
      if (count > 0) {
        rentDistribution.push({ range, count });
      }
    });

    const buildingRevenue = occupancyByBuilding.map(b => ({
      name: b.name,
      revenue: b.totalRent || 0,
      beds: b.total,
      avgRent: b.total > 0 ? Math.round((b.totalRent || 0) / b.total) : 0,
    }));

    const statusTrend = monthlyChartData.map(m => ({
      month: m.month,
      year: m.year,
      occupied: m.Occupied,
      available: m.Available,
      booked: m.Booked,
      partiallyBooked: m['Partially Booked'],
      left: m.Left,
      checkedOut: m['Checked Out'],
      maintenance: m.Maintenance,
      reserved: m.Reserved,
    }));

    const residentData = Object.values(residentMap).map(r => ({
      ...r,
      rent: r.rent || 0,
    }));

    if (activities.length < 6) {
      const sampleActivities = [
        { id: 'sample1', title: 'Rahul Sharma allocated to Bed A1, Room 101, Floor 1', time: '10:30 AM', type: 'allocation', icon: <PersonAdd sx={{ fontSize: 16 }} /> },
        { id: 'sample2', title: 'Room transfer completed from Room 202 to Room 203', time: '09:45 AM', type: 'transfer', icon: <TransferWithinAStation sx={{ fontSize: 16 }} /> },
        { id: 'sample3', title: 'Student checkout Amrit Verma from Room 301', time: 'Yesterday', type: 'checkout', icon: <Cancel sx={{ fontSize: 16 }} /> },
        { id: 'sample4', title: 'Maintenance request for Bed B1, Room 103', time: 'Yesterday', type: 'maintenance', icon: <Build sx={{ fontSize: 16 }} /> },
        { id: 'sample5', title: 'New admission Priya Patel checked in', time: 'May 24', type: 'checkin', icon: <PersonAdd sx={{ fontSize: 16 }} /> },
        { id: 'sample6', title: 'Fee payment received ₹12,500 from Priya Patel', time: 'May 24', type: 'payment', icon: <TrendingUp sx={{ fontSize: 16 }} /> },
      ];
      const allActivities = [...activities, ...sampleActivities];
      const uniqueActivities = [];
      const seenTitles = new Set();
      allActivities.forEach(act => {
        if (!seenTitles.has(act.title)) {
          seenTitles.add(act.title);
          uniqueActivities.push(act);
        }
      });
      activities.length = 0;
      activities.push(...uniqueActivities.slice(0, 6));
    }

    activities.sort((a, b) => {
      if (a.time === 'Today' && b.time !== 'Today') return -1;
      if (a.time !== 'Today' && b.time === 'Today') return 1;
      return 0;
    });

    const totalBuildings = Object.keys(buildingData).length;
    const totalRooms = Object.keys(roomData).length;
    const occupancyRate = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;

    return {
      totalBeds,
      totalBuildings,
      totalRooms,
      occupiedBeds,
      availableBeds,
      partiallyBookedBeds,
      leftBeds,
      bookedBeds,
      checkedOutBeds,
      maintenanceBeds,
      reservedBeds,
      todayCheckins,
      todayCheckouts,
      statusSummary: statusCount,
      buildingData,
      floorData,
      roomData,
      bedData,
      recentActivities: activities.slice(0, 6),
      occupancyByBuilding,
      bedStatusData,
      monthlyChartData,
      residentData,
      rentDistribution,
      buildingRevenue,
      statusTrend,
      performanceMetrics: {
        occupancyRate,
        availableRate: totalBeds > 0 ? Math.round((availableBeds / totalBeds) * 100) : 0,
        partiallyBookedRate: totalBeds > 0 ? Math.round((partiallyBookedBeds / totalBeds) * 100) : 0,
        leftRate: totalBeds > 0 ? Math.round((leftBeds / totalBeds) * 100) : 0,
        bookedRate: totalBeds > 0 ? Math.round((bookedBeds / totalBeds) * 100) : 0,
        checkedOutRate: totalBeds > 0 ? Math.round((checkedOutBeds / totalBeds) * 100) : 0,
        maintenanceRate: totalBeds > 0 ? Math.round((maintenanceBeds / totalBeds) * 100) : 0,
        reservedRate: totalBeds > 0 ? Math.round((reservedBeds / totalBeds) * 100) : 0,
      },
    };
  };

  const pieChartData = useMemo(() => {
    if (!dashboardData) return [];
    return dashboardData.bedStatusData || [];
  }, [dashboardData]);

  const filteredBuildingData = useMemo(() => {
    if (!dashboardData) return [];
    const { buildingData } = dashboardData;
    if (selectedBuilding === 'all') {
      return Object.keys(buildingData).map(key => ({
        ...buildingData[key],
        key,
      }));
    }
    return Object.keys(buildingData)
      .filter(key => key === selectedBuilding)
      .map(key => ({
        ...buildingData[key],
        key,
      }));
  }, [dashboardData, selectedBuilding]);

  const floorOptions = useMemo(() => {
    if (!dashboardData) return [];
    const { floorData } = dashboardData;
    const floors = Object.keys(floorData).map(key => floorData[key].name);
    return ['all', ...new Set(floors)];
  }, [dashboardData]);

  const buildingOptions = useMemo(() => {
    if (!dashboardData) return [];
    const { buildingData } = dashboardData;
    return ['all', ...Object.keys(buildingData)];
  }, [dashboardData]);


 const summaryCards = useMemo(() => {
  if (!dashboardData) return [];
  const {
    totalBeds,
    totalBuildings,
    occupiedBeds,
    availableBeds,
    partiallyBookedBeds,
    leftBeds,
    bookedBeds,
    checkedOutBeds,
    maintenanceBeds,
    performanceMetrics,
  } = dashboardData;

  return [
    {
      id: 'totalBeds',
      title: 'Total Beds',
      value: totalBeds,
      icon: <Hotel sx={{ fontSize: 20 }} />,
      rate: `${totalBuildings} Buildings`,
      gradient: 'linear-gradient(135deg, #6D5BD0 0%, #8B5CF6 100%)',
    },
    {
      id: 'occupiedBeds',
      title: 'Occupied',
      value: occupiedBeds,
      icon: <People sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.occupancyRate}%`,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
    },
      {
      id: 'partiallyBookedBeds',
      title: 'Partially',
      value: partiallyBookedBeds,
      icon: <HourglassEmpty sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.partiallyBookedRate}%`,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
    },
   {
      id: 'leftCheckedOut',
      title: 'Confirmed',
      isCombined: true,
      value1: leftBeds,
      label1: '',
      value2: checkedOutBeds,
      label2: 'Confirmed',
      icon: <Cancel sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.leftRate}% / ${performanceMetrics.checkedOutRate}%`,
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
    },
    {
      id: 'bookedBeds',
      title: 'Booked',
      value: bookedBeds,
      icon: <CalendarToday sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.bookedRate}%`,
      gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
    },
  
    
    {
      id: 'maintenanceBeds',
      title: 'Maintenance',
      value: maintenanceBeds,
      icon: <Build sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.maintenanceRate}%`,
      gradient: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
    },
     {
      id: 'availableBeds',
      title: 'Available',
      value: availableBeds,
      icon: <Bed sx={{ fontSize: 20 }} />,
      rate: `${performanceMetrics.availableRate}%`,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
    },
  ];
}, [dashboardData]);

  const handlePropertyChange = (event) => {
    const selectedId = event.target.value;
    dispatch(setLocalProperty({
      componentId: 'bedManagement',
      propertyId: selectedId,
    }));
    setDashboardData(null);
    setAllFetchedData([]);
    setTotalRecords(0);
  };

  const handleBuildingChange = (event) => {
    setSelectedBuilding(event.target.value);
    setSelectedFloor('all');
  };

  const handleFloorChange = (event) => {
    setSelectedFloor(event.target.value);
  };

  const handleToggleFloor = (floorKey) => {
    setExpandedFloors(prev => ({
      ...prev,
      [floorKey]: !prev[floorKey],
    }));
  };

  const handleToggleRoom = (roomKey) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomKey]: !prev[roomKey],
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleHomeClick = () => navigate('/dashboard');
  const handleLocationClick = () => navigate('/masters/property');

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || '#8884d8';
  };

  const getStatusLabel = (status) => {
    return STATUS_LABELS[status] || status;
  };

  const getStatusIcon = (status) => {
    return STATUS_ICONS[status] || <Bed sx={{ fontSize: 18 }} />;
  };

  return (
    <Box sx={{
      maxWidth: '100vw',
      overflowX: 'hidden',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh',
      p: 3,
    }}>

       <ToastContainer />
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea, #764ba2, #f05c50, #046A38)',
          },
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                }}
              >
                <Hotel sx={{ fontSize: 34 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B', letterSpacing: '-0.5px' }}>
                  Bed Report
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                  Premium Hotel Analytics Dashboard
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                <InputLabel>Property</InputLabel>
                <Select
                  value={selectedProperty || ''}
                  onChange={handlePropertyChange}
                  label="Property"
                >
                  {properties.map((item) => (
                    <MenuItem key={item.Id} value={item.Id}>{item.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From"
                  value={dateFrom}
                  onChange={setDateFrom}
                  format="MM/YYYY"
                  views={['month', 'year']}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: 130,
                        '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                      }
                    }
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateTo}
                  onChange={setDateTo}
                  format="MM/YYYY"
                  views={['month', 'year']}
                  minDate={dateFrom}
                  maxDate={dateFrom ? dateFrom.add(12, 'months') : null}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: 130,
                        '& .MuiOutlinedInput-root': { borderRadius: '12px' }
                      }
                    }
                  }}
                />
              </LocalizationProvider>

              <Button
                variant="contained"
                onClick={fetchAllDashboardData}
                startIcon={<Refresh />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Load Data
              </Button>

              <Tooltip title="Export Data">
                <IconButton
                  onClick={handleExport}
                  sx={{
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '12px',
                    color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  }}
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print Report">
                <IconButton
                  onClick={handlePrint}
                  sx={{
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '12px',
                    color: '#667eea',
                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <Breadcrumbs separator={<ChevronRight fontSize="small" />} aria-label="breadcrumb">
            <Link onClick={handleHomeClick} underline="hover" sx={{ color: '#667eea', cursor: 'pointer' }}>
              Home
            </Link>
            <Link onClick={handleLocationClick} underline="hover" sx={{ color: '#667eea', cursor: 'pointer' }}>
              Property
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 600 }}>
              Bed Management Dashboard
            </Typography>
          </Breadcrumbs>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
          bgcolor: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: '24px',
        }}>
          <CircularProgress sx={{ color: '#667eea', size: 60 }} />
          <Typography sx={{ mt: 3, color: '#667eea', fontWeight: 600 }}>
            Loading all bed data...
          </Typography>
          <Typography sx={{ mt: 1, color: '#94A3B8', fontSize: 14 }}>
            Fetching {totalRecords > 0 ? totalRecords : 'all'} records
          </Typography>
          <LinearProgress sx={{ width: 300, mt: 2, borderRadius: 4, height: 6 }} />
        </Box>
      ) : dashboardData ? (
        <Fade in timeout={500}>
          <Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {summaryCards.map((card, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={1.7} key={card.id}>
                    <Slide direction="up" in timeout={300 + index * 80}>
                      <Card
                        sx={{
                          borderRadius: '20px',
                          background: card.gradient,
                          color: '#fff',
                          height: '100%',
                          minHeight: 120,
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 14px 30px rgba(0,0,0,0.22)',
                          },
                        }}
                      >

                        <Box
                          sx={{
                            position: 'absolute',
                            top: -35,
                            right: -35,
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.08)',
                          }}
                        />

                        <CardContent sx={{ p: 2, position: 'relative', zIndex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                fontSize: '0.68rem',
                                textTransform: 'uppercase',
                                opacity: 0.9,
                              }}
                            >
                              {card.title}
                            </Typography>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 25, height: 25 }}>
                              {card.icon}
                            </Avatar>
                          </Box>

                          {card.isCombined ? (
                            <Box sx={{  display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                  {card.value1}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.6rem' }}>
                                  {card.label1}
                                </Typography>
                              </Box>
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ borderColor: 'rgba(255,255,255,0.35)' }}
                              />
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                                  {card.value2}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.6rem' }}>
                                  {card.label2}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="h6" sx={{ fontWeight: 800, mt: .5 }}>
                              {card.value}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: .5 }}>
                            <ArrowUpward sx={{ fontSize: 13 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.9 }}>
                              {card.rate}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Slide>
                  </Grid>
                ))}
              </Grid>


            <Paper
              sx={{
                borderRadius: '24px',
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 2,
                  pt: 1,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    minHeight: 48,
                    borderRadius: '12px 12px 0 0',
                    '&.Mui-selected': {
                      color: '#667eea',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#667eea',
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                }}
              >
                <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" {...a11yProps(0)} />
                <Tab icon={<People />} iconPosition="start" label="Residents & Rent" {...a11yProps(1)} />
                <Tab icon={<Analytics />} iconPosition="start" label="Analytics & Trends" {...a11yProps(2)} />
                <Tab icon={<AttachMoney />} iconPosition="start" label="Revenue & Distribution" {...a11yProps(3)} />
                <Tab icon={<ViewList />} iconPosition="start" label="Detailed View" {...a11yProps(4)} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} lg={7}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                          Building & Room Overview
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={selectedFloor}
                              onChange={handleFloorChange}
                              displayEmpty
                              sx={{ borderRadius: '10px' }}
                            >
                              <MenuItem value="all">All Floors</MenuItem>
                              {floorOptions.filter(f => f !== 'all').map(f => (
                                <MenuItem key={f} value={f}>{f}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>

                      <Box>
                        {filteredBuildingData.map((building) => (
                          <Accordion
                            key={building.key}
                            expanded={expandedFloors[building.key] !== false}
                            onChange={() => handleToggleFloor(building.key)}
                            sx={{
                              mb: 1,
                              borderRadius: '16px !important',
                              background: 'rgba(255,255,255,0.5)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              boxShadow: 'none',
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': {
                                margin: '0 0 8px 0',
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{
                                borderRadius: '16px',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                                <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}>
                                  <ApartmentIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', minWidth: 100 }}>
                                  {building.key}
                                </Typography>
                                <Chip
                                  label={`Total: ${building.totalBeds}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontSize: 11 }}
                                />
                                <Chip
                                  label={`O: ${building.occupiedBeds}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(240, 92, 80, 0.1)', color: '#f05c50', fontSize: 11 }}
                                />
                                <Chip
                                  label={`A: ${building.availableBeds}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(4, 106, 56, 0.1)', color: '#046A38', fontSize: 11 }}
                                />
                                <Chip
                                  label={`B: ${building.bookedBeds || 0}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(99, 91, 255, 0.1)', color: '#635bff', fontSize: 11 }}
                                />
                                <Chip
                                  label={`P: ${building.partiallyBookedBeds || 0}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255, 103, 31, 0.1)', color: '#FF671F', fontSize: 11 }}
                                />
                                <Chip
                                  label={`M: ${building.maintenanceBeds || 0}`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(128, 0, 0, 0.1)', color: '#800000', fontSize: 11 }}
                                />
                                <LinearProgress
                                  variant="determinate"
                                  value={building.totalBeds > 0 ? (building.occupiedBeds / building.totalBeds) * 100 : 0}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 4,
                                    bgcolor: '#E2E8F0',
                                    minWidth: 80,
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: (building.occupiedBeds / building.totalBeds) > 0.8 ? '#f05c50' : '#667eea',
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, minWidth: 35 }}>
                                  {building.totalBeds > 0 ? Math.round((building.occupiedBeds / building.totalBeds) * 100) : 0}%
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {Object.keys(dashboardData.floorData)
                                .filter(key => dashboardData.floorData[key].building === building.key)
                                .sort((a, b) => {
                                  const numA = parseInt(a.replace('Floor ', ''));
                                  const numB = parseInt(b.replace('Floor ', ''));
                                  return numA - numB;
                                })
                                .map((floorKey) => {
                                  const floor = dashboardData.floorData[floorKey];
                                  const roomKeys = Object.keys(dashboardData.roomData)
                                    .filter(key => dashboardData.roomData[key].floor === floorKey);

                                  return (
                                    <Accordion
                                      key={floorKey}
                                      expanded={expandedRooms[floorKey] !== false}
                                      onChange={() => handleToggleRoom(floorKey)}
                                      sx={{
                                        mb: 0.5,
                                        borderRadius: '12px !important',
                                        background: 'rgba(255,255,255,0.3)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                      }}
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                          borderRadius: '12px',
                                          '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.03)' },
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', minWidth: 70 }}>
                                            {floorKey} Floor
                                          </Typography>
                                          <Chip
                                            label={`Total: ${floor.totalBeds}`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(102, 126, 234, 0.08)', color: '#667eea', fontSize: 10 }}
                                          />
                                          <Chip
                                            label={`O: ${floor.occupiedBeds}`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(240, 92, 80, 0.08)', color: '#f05c50', fontSize: 10 }}
                                          />
                                          <Chip
                                            label={`A: ${floor.availableBeds}`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(4, 106, 56, 0.08)', color: '#046A38', fontSize: 10 }}
                                          />
                                          <Chip
                                            label={`B: ${floor.bookedBeds || 0}`}
                                            size="small"
                                            sx={{ bgcolor: 'rgba(99, 91, 255, 0.08)', color: '#635bff', fontSize: 10 }}
                                          />
                                          <LinearProgress
                                            variant="determinate"
                                            value={floor.totalBeds > 0 ? (floor.occupiedBeds / floor.totalBeds) * 100 : 0}
                                            sx={{
                                              flex: 1,
                                              height: 4,
                                              borderRadius: 4,
                                              bgcolor: '#E2E8F0',
                                              minWidth: 50,
                                              '& .MuiLinearProgress-bar': {
                                                bgcolor: (floor.occupiedBeds / floor.totalBeds) > 0.8 ? '#f05c50' : '#667eea',
                                                borderRadius: 4,
                                              },
                                            }}
                                          />
                                          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: 10 }}>
                                            {floor.totalBeds > 0 ? Math.round((floor.occupiedBeds / floor.totalBeds) * 100) : 0}%
                                          </Typography>
                                        </Box>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <Grid container spacing={1}>
                                          {roomKeys.map((roomKey) => {
                                            const room = dashboardData.roomData[roomKey];
                                            const bedStatuses = room.bedStatuses || [];
                                            const bedRents = room.bedRents || [];
                                            const residents = room.residents || [];

                                            const hasOccupied = bedStatuses.some(s => s === 'O');
                                            const hasAvailable = bedStatuses.some(s => s === 'A');
                                            const hasPartiallyBooked = bedStatuses.some(s => s === 'P');
                                            const hasLeft = bedStatuses.some(s => s === 'L');
                                            const hasBooked = bedStatuses.some(s => s === 'B');
                                            const hasMaintenance = bedStatuses.some(s => s === 'M');
                                            const hasCheckedOut = bedStatuses.some(s => s === 'C');
                                            
                                            let roomStatus = 'A';
                                            let roomStatusLabel = 'Available';
                                            let roomStatusColor = '#046A38';
                                            
                                            if (hasOccupied) {
                                              roomStatus = 'O';
                                              roomStatusLabel = 'Occupied';
                                              roomStatusColor = '#f05c50';
                                            } else if (hasBooked) {
                                              roomStatus = 'B';
                                              roomStatusLabel = 'Booked';
                                              roomStatusColor = '#635bff';
                                            } else if (hasPartiallyBooked) {
                                              roomStatus = 'P';
                                              roomStatusLabel = 'Partially Booked';
                                              roomStatusColor = '#FF671F';
                                            } else if (hasLeft) {
                                              roomStatus = 'L';
                                              roomStatusLabel = 'Left';
                                              roomStatusColor = '#8B5CF6';
                                            } else if (hasCheckedOut) {
                                              roomStatus = 'C';
                                              roomStatusLabel = 'Checked Out';
                                              roomStatusColor = '#c971bb';
                                            } else if (hasMaintenance) {
                                              roomStatus = 'M';
                                              roomStatusLabel = 'Maintenance';
                                              roomStatusColor = '#800000';
                                            }

                                            const totalRent = bedRents.reduce((sum, r) => sum + (r || 0), 0);

                                            return (
                                              <Grid item xs={12} sm={6} md={4} key={roomKey}>
                                                <Paper
                                                  sx={{
                                                    p: 1.5,
                                                    borderRadius: '12px',
                                                    background: 'rgba(255,255,255,0.5)',
                                                    border: `1px solid ${roomStatusColor}30`,
                                                    '&:hover': { boxShadow: '0 4px 15px rgba(0,0,0,0.06)' },
                                                  }}
                                                >
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                      Room {room.roomNo}
                                                    </Typography>
                                                    <Chip
                                                      label={roomStatusLabel}
                                                      size="small"
                                                      sx={{
                                                        bgcolor: `${roomStatusColor}15`,
                                                        color: roomStatusColor,
                                                        fontWeight: 600,
                                                        fontSize: 9,
                                                        borderRadius: '6px',
                                                        height: 20,
                                                      }}
                                                    />
                                                  </Box>
                                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {room.beds.map((bedName, idx) => {
                                                      const bedStatus = bedStatuses[idx] || 'A';
                                                      const rent = bedRents[idx] || 0;
                                                      const resident = residents[idx] || 'Vacant';
                                                      return (
                                                        <Tooltip key={idx} title={`${bedName} - ${getStatusLabel(bedStatus)} | ₹${rent} | ${resident}`}>
                                                          <Chip
                                                            label={bedName}
                                                            size="small"
                                                            sx={{
                                                              bgcolor: `${getStatusColor(bedStatus)}15`,
                                                              color: getStatusColor(bedStatus),
                                                              borderColor: getStatusColor(bedStatus),
                                                              border: '1px solid',
                                                              borderRadius: '6px',
                                                              fontSize: 9,
                                                              fontWeight: 600,
                                                              height: 20,
                                                            }}
                                                          />
                                                        </Tooltip>
                                                      );
                                                    })}
                                                  </Box>
                                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 9 }}>
                                                      ₹{totalRent}/mo
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 9 }}>
                                                      {residents.filter(r => r !== 'Vacant').length} residents
                                                    </Typography>
                                                  </Box>
                                                </Paper>
                                              </Grid>
                                            );
                                          })}
                                        </Grid>
                                      </AccordionDetails>
                                    </Accordion>
                                  );
                                })}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            background: 'rgba(255,255,255,0.5)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PieChartIcon sx={{ color: '#667eea' }} />
                            Bed Status Distribution
                          </Typography>
                          <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                              <Pie
                                data={pieChartData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                              >
                                {pieChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.key] || CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip
                                contentStyle={{
                                  borderRadius: '12px',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                  background: 'rgba(255,255,255,0.9)',
                                  backdropFilter: 'blur(10px)',
                                }}
                                formatter={(value, name) => [`${value} beds`, name]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1 }}>
                            {pieChartData.map((item, idx) => (
                              <Chip
                                key={idx}
                                label={`${item.name}: ${item.value}`}
                                size="small"
                                sx={{
                                  bgcolor: `${STATUS_COLORS[item.key] || CHART_COLORS[idx]}15`,
                                  color: STATUS_COLORS[item.key] || CHART_COLORS[idx],
                                  fontWeight: 600,
                                  border: `1px solid ${STATUS_COLORS[item.key] || CHART_COLORS[idx]}30`,
                                }}
                              />
                            ))}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            background: 'rgba(255,255,255,0.5)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Notifications sx={{ color: '#667eea' }} />
                            Recent Activities
                          </Typography>
                          <List sx={{ p: 0 }}>
                            {dashboardData.recentActivities && dashboardData.recentActivities.length > 0 ? (
                              dashboardData.recentActivities.map((activity, idx) => (
                                <ListItem
                                  key={activity.id || idx}
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    mb: 0.5,
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Avatar
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: activity.type === 'checkin' ? 'rgba(6, 182, 212, 0.1)' :
                                          activity.type === 'checkout' ? 'rgba(245, 158, 11, 0.1)' :
                                          activity.type === 'transfer' ? 'rgba(139, 92, 246, 0.1)' :
                                          activity.type === 'maintenance' ? 'rgba(120, 113, 108, 0.1)' :
                                          'rgba(102, 126, 234, 0.1)',
                                        color: activity.type === 'checkin' ? '#06B6D4' :
                                          activity.type === 'checkout' ? '#F59E0B' :
                                          activity.type === 'transfer' ? '#8B5CF6' :
                                          activity.type === 'maintenance' ? '#78716C' :
                                          '#667eea',
                                      }}
                                    >
                                      {activity.icon}
                                    </Avatar>
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E293B' }}>
                                        {activity.title}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="caption" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTime sx={{ fontSize: 12 }} />
                                        {activity.time}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ color: '#94A3B8', textAlign: 'center', py: 2 }}>
                                No recent activities
                              </Typography>
                            )}
                          </List>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People sx={{ color: '#667eea' }} />
                        Residents List ({dashboardData.residentData?.length || 0})
                      </Typography>
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Resident</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Bed</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Room</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Rent (₹)</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(dashboardData.residentData || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((resident, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'rgba(102, 126, 234, 0.1)', color: '#667eea', fontSize: 12 }}>
                                      {resident.name.charAt(0)}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{resident.name}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{resident.bed}</TableCell>
                                <TableCell>{resident.room}</TableCell>
                                <TableCell>{resident.floor}</TableCell>
                                <TableCell align="right">₹{resident.rent || 0}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={getStatusLabel(resident.status)}
                                    size="small"
                                    sx={{
                                      bgcolor: `${getStatusColor(resident.status)}15`,
                                      color: getStatusColor(resident.status),
                                      fontWeight: 600,
                                      fontSize: 10,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={dashboardData.residentData?.length || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoney sx={{ color: '#667eea' }} />
                        Rent Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.rentDistribution || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <YAxis dataKey="range" type="category" tick={{ fontSize: 11, fill: '#64748B' }} width={80} />
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.3)',
                              background: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(10px)',
                            }}
                            formatter={(value) => [`${value} beds`, 'Count']}
                          />
                          <Bar dataKey="count" fill="#667eea" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Analytics sx={{ color: '#667eea' }} />
                        Monthly Rent Analysis
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <ToggleButtonGroup
                          value={chartType}
                          exclusive
                          onChange={handleChartTypeChange}
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              px: 2,
                            },
                          }}
                        >
                          <ToggleButton value="bar"><BarChartIcon sx={{ fontSize: 16, mr: 0.5 }} /> Bar</ToggleButton>
                          <ToggleButton value="line"><ShowChart sx={{ fontSize: 16, mr: 0.5 }} /> Line</ToggleButton>
                          <ToggleButton value="area"><BubbleChart sx={{ fontSize: 16, mr: 0.5 }} /> Area</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={dashboardData.monthlyChartData || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.3)',
                              background: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(10px)',
                            }}
                            formatter={(value, name) => {
                              if (name === 'Rent') return [`₹${value}`, 'Total Rent'];
                              return [`${value} beds`, name];
                            }}
                          />
                          <Legend />
                          <Bar yAxisId="left" dataKey="total" fill="#667eea" name="Total Beds" radius={[4, 4, 0, 0]} opacity={0.6} />
                          <Line yAxisId="right" type="monotone" dataKey="totalRent" stroke="#f05c50" name="Rent" strokeWidth={3} dot={{ r: 4 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimelineIcon sx={{ color: '#667eea' }} />
                          Status Trend Over Time
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={handleChartTypeChange}
                            size="small"
                            sx={{
                              '& .MuiToggleButton-root': {
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                px: 2,
                              },
                            }}
                          >
                            <ToggleButton value="bar"><BarChartIcon sx={{ fontSize: 16, mr: 0.5 }} /> Bar</ToggleButton>
                            <ToggleButton value="line"><ShowChart sx={{ fontSize: 16, mr: 0.5 }} /> Line</ToggleButton>
                            <ToggleButton value="area"><BubbleChart sx={{ fontSize: 16, mr: 0.5 }} /> Area</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={dashboardData.statusTrend || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.3)',
                              background: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(10px)',
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="occupied" stackId="a" fill="#f05c50" name="Occupied" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="available" stackId="a" fill="#046A38" name="Available" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="booked" stackId="a" fill="#635bff" name="Booked" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="partiallyBooked" stackId="a" fill="#FF671F" name="Partially Booked" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="left" stackId="a" fill="#8B5CF6" name="Left" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="checkedOut" stackId="a" fill="#c971bb" name="Checked Out" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="maintenance" stackId="a" fill="#800000" name="Maintenance" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="reserved" stackId="a" fill="#47a1ba" name="Reserved" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Speed sx={{ color: '#667eea' }} />
                        Performance Metrics
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(dashboardData.performanceMetrics || {}).map(([key, value]) => (
                          <Grid item xs={6} sm={4} key={key}>
                            <Box sx={{
                              p: 1.5,
                              bgcolor: 'rgba(255,255,255,0.3)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.2)',
                              textAlign: 'center',
                            }}>
                              <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E293B' }}>
                                {value}%
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#94A3B8', textTransform: 'capitalize', fontSize: '0.6rem' }}>
                                {key.replace('Rate', '').replace(/([A-Z])/g, ' $1').trim()}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={value}
                                sx={{
                                  mt: 0.5,
                                  height: 3,
                                  borderRadius: 2,
                                  bgcolor: '#E2E8F0',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: value >= 70 ? '#f05c50' : value >= 40 ? '#FF671F' : '#046A38',
                                    borderRadius: 2,
                                  },
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationCity sx={{ color: '#667eea' }} />
                        Occupancy by Building
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(dashboardData.occupancyByBuilding || []).map((item, idx) => (
                          <Box key={idx}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 20, height: 20, bgcolor: CHART_COLORS[idx % CHART_COLORS.length], fontSize: 10, color: '#fff' }}>
                                  {item.name.charAt(0)}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                  {item.name}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                  {item.occupied}/{item.total}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                  {item.occupancy}%
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={item.occupancy}
                              sx={{
                                height: 6,
                                borderRadius: 4,
                                bgcolor: '#E2E8F0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: item.occupancy >= 80 ? '#f05c50' :
                                    item.occupancy >= 60 ? '#FF671F' :
                                    '#046A38',
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoney sx={{ color: '#667eea' }} />
                        Building Revenue
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dashboardData.buildingRevenue || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(value) => `₹${value/1000}K`} />
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.3)',
                              background: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(10px)',
                            }}
                            formatter={(value, name) => {
                              if (name === 'revenue') return [`₹${value}`, 'Revenue'];
                              if (name === 'avgRent') return [`₹${value}`, 'Avg Rent'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="revenue" fill="#667eea" name="Revenue" radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="avgRent" stroke="#f05c50" name="Avg Rent" strokeWidth={2} dot={{ r: 4 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DonutLarge sx={{ color: '#667eea' }} />
                        Revenue Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={dashboardData.buildingRevenue || []}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="revenue"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                          >
                            {(dashboardData.buildingRevenue || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: '12px',
                              border: '1px solid rgba(255,255,255,0.3)',
                              background: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(10px)',
                            }}
                            formatter={(value) => [`₹${value}`, 'Revenue']}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '20px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableChartIcon sx={{ color: '#667eea' }} />
                        Detailed Revenue Report
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Total Beds</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Total Revenue</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Avg Rent/Bed</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Occupancy</TableCell>
                              <TableCell sx={{ fontWeight: 700 }} align="right">Residents</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(dashboardData.buildingRevenue || []).map((item, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 24, height: 24, bgcolor: CHART_COLORS[idx % CHART_COLORS.length], fontSize: 11, color: '#fff' }}>
                                      {item.name.charAt(0)}
                                    </Avatar>
                                    {item.name}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{item.beds}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                  ₹{item.revenue.toLocaleString()}
                                </TableCell>
                                <TableCell align="right">₹{item.avgRent}</TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={`${Math.round((item.beds > 0 ? (dashboardData.occupancyByBuilding?.find(b => b.name === item.name)?.occupancy || 0) : 0))}%`}
                                    size="small"
                                    sx={{
                                      bgcolor: item.occupancy >= 80 ? 'rgba(240, 92, 80, 0.1)' :
                                        item.occupancy >= 60 ? 'rgba(255, 103, 31, 0.1)' :
                                        'rgba(4, 106, 56, 0.1)',
                                      color: item.occupancy >= 80 ? '#f05c50' :
                                        item.occupancy >= 60 ? '#FF671F' :
                                        '#046A38',
                                      fontWeight: 600,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">{dashboardData.occupancyByBuilding?.find(b => b.name === item.name)?.residentCount || 0}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.5)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ViewList sx={{ color: '#667eea' }} />
                      All Bed Details
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                     
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={selectedFloor}
                          onChange={handleFloorChange}
                          displayEmpty
                          sx={{ borderRadius: '10px' }}
                        >
                          <MenuItem value="all">All Floors</MenuItem>
                          {floorOptions.filter(f => f !== 'all').map(f => (
                            <MenuItem key={f} value={f}>{f}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title="Export">
                        <IconButton onClick={handleExport} sx={{ color: '#667eea', bgcolor: 'rgba(102,126,234,0.1)' }}>
                          <FileDownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <TableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Bed Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Room</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Resident</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Rent (₹)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Check-in</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Check-out</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.values(dashboardData.bedData || {})
                          .filter(bed => selectedBuilding === 'all' || bed.building === selectedBuilding)
                          .filter(bed => selectedFloor === 'all' || bed.floor === selectedFloor)
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((bed, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ fontWeight: 500 }}>{bed.name}</TableCell>
                              <TableCell>{bed.roomNo}</TableCell>
                              <TableCell>{bed.floor}</TableCell>
                              <TableCell>{bed.building}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {bed.resident !== 'Vacant' && (
                                    <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea' }}>
                                      {bed.resident.charAt(0)}
                                    </Avatar>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: bed.resident !== 'Vacant' ? 600 : 400, color: bed.resident !== 'Vacant' ? '#1E293B' : '#94A3B8' }}>
                                    {bed.resident}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: bed.rent > 0 ? 700 : 400, color: bed.rent > 0 ? '#1E293B' : '#94A3B8' }}>
                                {bed.rent > 0 ? `₹${bed.rent}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(bed.status)}
                                  size="small"
                                  icon={getStatusIcon(bed.status)}
                                  sx={{
                                    bgcolor: `${getStatusColor(bed.status)}15`,
                                    color: getStatusColor(bed.status),
                                    fontWeight: 600,
                                    fontSize: 10,
                                    '& .MuiChip-icon': { fontSize: 14, color: getStatusColor(bed.status) },
                                  }}
                                />
                              </TableCell>
                              <TableCell>{bed.checkinDate || '-'}</TableCell>
                              <TableCell>{bed.checkoutDate || '-'}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={Object.values(dashboardData.bedData || {})
                      .filter(bed => selectedBuilding === 'all' || bed.building === selectedBuilding)
                      .filter(bed => selectedFloor === 'all' || bed.floor === selectedFloor).length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </Paper>
              </TabPanel>
            </Paper>
          </Box>
        </Fade>
      ) : (
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            borderRadius: '24px',
            p: 4,
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)',
              }}
            >
              <Hotel sx={{ fontSize: 50, color: '#fff' }} />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
              Bed Management Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', mb: 1 }}>
              Select a property and date range to get started
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              View occupancy statistics, bed distribution, and recent activities
            </Typography>
            <Button
              variant="contained"
              onClick={fetchAllDashboardData}
              startIcon={<Refresh />}
              sx={{
                mt: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                textTransform: 'none',
                px: 4,
                py: 1.5,
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Load Dashboard
            </Button>
          </Box>
        </Paper>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .MuiPaper-root {
            transition: all 0.3s ease;
          }
          .MuiCard-root {
            transition: all 0.3s ease;
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: rgba(0,0,0,0.05);
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 10px;
          }
          .MuiToggleButton-root.Mui-selected {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white !important;
          }
          .MuiToggleButton-root.Mui-selected:hover {
            background: linear-gradient(135deg, #5a6fd6 0%, #6a3d92 100%);
          }
        `}
      </style>
    </Box>
  );
}